import usePurchaseEvents from '../hooks/usePurchaseEvents';
import useInventoryCategory from '../hooks/useInventoryCategory';
import InventoryWorkspace from '../components/inventory/InventoryWorkspace';
import WarehouseOperationPanel from '../components/inventory/WarehouseOperationPanel';
import InventoryHistory from '../components/inventory/InventoryHistory';
import VarietyEditorDialog from '../components/varietyPricing/VarietyEditorDialog';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiAlertTriangle,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiDollarSign,
  FiSave,
  FiSearch,
  FiSend,
  FiTrendingDown,
  FiX,
} from 'react-icons/fi';
import {
  createInventoryCount,
  createInventoryMerma,
  finalizeInventoryCount,
  getCategories,
  getInventoryCountSession,
  getInventoryCountSessions,
  getInventoryEmailSuggestions,
  getInventorySummary,
  sendInventoryEmail,
  updateInventoryCount,
  updateVarietyInventorySettings,
  updateVarietyPriceEquivalence,
} from '../api';
import InlineLoading from '../components/InlineLoading';
import ProductSearchInput from '../components/ProductSearchInput';
import SearchMultiSelect from '../components/SearchMultiSelect';
import useCurrentUser from '../hooks/useCurrentUser';
import useProductSearchSettings from '../hooks/useProductSearchSettings';
import { hasPermission } from '../utils/permissions';
import { notify } from '../utils/alertBus';
import { normalizePaginatedResponse } from '../utils/pagination';
import {
  formatInventoryMeasure,
  formatInventoryCurrency,
  formatInventoryQuantity,
  formatKg,
  formatStockedUnits,
  getInventoryDisplayUnit,
  getCountSessionStatusMeta,
  getInventoryProductImage,
  getInventoryKgPreview,
  getInventoryVariancePreview,
} from '../utils/inventoryFormatting';
import { fieldsMatchProductSearchTerms } from '../utils/productSearch';

const PLACEHOLDER_IMAGE = 'https://placehold.co/96';
const INVENTORY_STATUS_LABELS = {
  all: 'Todos',
  ok: 'OK',
  negative: 'Negativo',
  warning: 'Revisar',
};

const getRowLabel = (row) =>
  [row?.productName, row?.varietyName].filter(Boolean).join(' - ') || `Variedad ${row?.varietyId || ''}`;

const toInventoryUnitKey = (value) => String(value || '').trim().toLowerCase();

const DIRECT_BASE_UNIT_KEYS = new Set(['kg', 'kilo', 'kilogramo', 'granel', 'bin']);
const UNIT_BASE_UNIT_KEYS = new Set(['unidad', 'un', 'u', 'unit']);

const getInventoryBaseUnit = (row = {}) =>
  row.inventoryBaseUnit || row.inventoryMetricUnit || row.baseUnit || 'kg';

const getSuggestedInventoryBaseUnit = (row = {}) =>
  row.categoryIsResaleOnly ? 'unidad' : getInventoryBaseUnit(row);

const isDirectInventoryEquivalence = (baseUnit, metricUnit, { categoryIsResaleOnly = false } = {}) => {
  const baseKey = toInventoryUnitKey(baseUnit);
  const unitKey = toInventoryUnitKey(metricUnit);
  if (!baseKey || !unitKey) return false;
  if (baseKey === unitKey) return true;
  if (baseKey === 'kg' && DIRECT_BASE_UNIT_KEYS.has(unitKey)) return true;
  if (baseKey === 'unidad' && UNIT_BASE_UNIT_KEYS.has(unitKey)) return true;
  if (baseKey === 'unidad' && categoryIsResaleOnly) return true;
  return false;
};

const parsePositiveConversionValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const findUnitOption = (row = {}, metricUnit) => {
  const unitKey = toInventoryUnitKey(metricUnit);
  return (row.unitOptions || []).find((option) =>
    toInventoryUnitKey(option.metricUnitKey || option.metricUnit) === unitKey
    || toInventoryUnitKey(option.metricUnit) === unitKey
  ) || null;
};

const getRowInventoryKey = (row = {}) =>
  String(row?.inventoryKey || row?.varietyId || '');

const getDefaultUnit = (row) => {
  if (row?.usesUnitInventory) return getInventoryDisplayUnit(row);
  return row?.unitOptions?.find((option) => option.metricUnitKey === 'kg')?.metricUnit
    || row?.unitOptions?.[0]?.metricUnit
    || 'kg';
};

const getUserDisplayName = (user) => {
  if (!user) return 'Usuario actual';
  return user.name || user.username || `Usuario ${user.id}`;
};

const isQuantityEntered = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const hasPositiveQuantity = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

const toDateTimeLocalValue = (value = new Date()) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIsoDateTime = (value) => {
  const parsed = value ? new Date(value) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('es-CL');
};

const fetchAllInventoryRows = async (branchId) => {
  const response = await getInventorySummary(branchId, {
    pageSize: 0,
    status: 'all',
  });
  const normalized = normalizePaginatedResponse(response);
  return normalized.data || [];
};

const buildEntryItems = ({ entries, rowsById, requirePositive = false, allowedRowKeys = null }) =>
  Object.entries(entries)
    .filter(([rowKey]) => rowsById.has(String(rowKey)))
    .filter(([rowKey]) => !allowedRowKeys || allowedRowKeys.has(String(rowKey)))
    .filter(([, entry]) => (
      requirePositive
        ? hasPositiveQuantity(entry.quantity)
        : isQuantityEntered(entry.quantity)
    ))
    .map(([rowKey, entry]) => {
      const row = rowsById.get(String(rowKey));
      return {
        inventoryKey: getRowInventoryKey(row),
        varietyId: Number(row?.varietyId),
        varietyPriceId: row?.varietyPriceId, trackingMode: row?.trackingMode, policyVersion: row?.policyVersion,
        quantity: Number(entry.quantity),
        metricUnit: entry.metricUnit || getDefaultUnit(row),
        reason: entry.reason,
        note: entry.note,
      };
    });

const findMissingConversion = ({ items, rowsById }) =>
  items.reduce((match, item) => {
    if (match) return match;
    const row = rowsById.get(String(item.inventoryKey || item.varietyId));
    if (getInventoryKgPreview(row, item.quantity, item.metricUnit) !== null) return null;
    return { item, row };
  }, null);

const getCategoryLabel = (category) => category?.name || category?.label || `Categoria ${category?.id}`;

const toCategoryOption = (category) => ({
  id: category.id,
  label: getCategoryLabel(category),
  name: getCategoryLabel(category),
});

const getRowCategoryId = (row) => (row?.categoryId === undefined || row?.categoryId === null ? '' : String(row.categoryId));

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const isValidEmail = (value) => EMAIL_PATTERN.test(normalizeEmail(value));

const toEmailOption = (entry) => {
  const email = normalizeEmail(entry?.email || entry?.username || entry?.id || entry);
  if (!isValidEmail(email)) return null;
  const name = entry?.name || '';
  return {
    id: email,
    email,
    name,
    source: entry?.source || 'manual',
    label: name ? `${name} <${email}>` : email,
  };
};

const ProductIdentity = ({ row, imageSize = 'h-14 w-14' }) => (
  <div className="flex items-start gap-3">
    <img
      src={getInventoryProductImage(row)}
      alt={row?.varietyName || row?.productName || 'Producto'}
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = PLACEHOLDER_IMAGE;
      }}
      className={`${imageSize} flex-none rounded border border-slate-200 bg-slate-50 object-cover`}
    />
    <div className="min-w-0">
      <p className="font-semibold text-slate-900 break-words">{row?.productName || 'Producto'}</p>
      <p className="text-xs text-slate-500 break-words">Variedad: {row?.varietyName || 'Variedad'}</p>
      {row?.packLabel && <p className="text-xs font-medium text-blue-700">{row.packLabel}</p>}
      {row?.categoryName && (
        <p className="text-xs text-slate-400 break-words">{row.categoryName}</p>
      )}
    </div>
  </div>
);

const DetailLine = ({ label, value, className = '' }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <div className={`mt-0.5 text-sm text-slate-900 ${className}`}>{value}</div>
  </div>
);

const CategoryMultiSelect = ({
  categories = [],
  selectedCategoryIds = [],
  onChange,
  label = 'Categorias',
  placeholder = 'Buscar categoria',
  disabled = false,
}) => {
  const options = useMemo(() => categories.map(toCategoryOption), [categories]);
  const selected = useMemo(() => {
    const selectedSet = new Set((selectedCategoryIds || []).map(String));
    return options.filter((option) => selectedSet.has(String(option.id)));
  }, [options, selectedCategoryIds]);

  return (
    <SearchMultiSelect
      label={label}
      placeholder={placeholder}
      value={selected}
      options={options}
      disabled={disabled}
      getOptionLabel={(option) => option.label || option.name || String(option.id)}
      onChange={(next) => onChange?.((next || []).map((option) => String(option.id)))}
    />
  );
};

const CountSessionsPanel = ({ sessions, loading, onOpen }) => (
  <div className="bg-white border border-slate-200 rounded-lg">
    <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Conteos</h2>
        <p className="text-xs text-slate-500">Listas fechadas con responsable registrado</p>
      </div>
      <FiCalendar className="text-slate-400" />
    </div>
    <div className="divide-y divide-slate-200">
      {loading ? (
        <div className="p-4"><InlineLoading label="Cargando conteos" /></div>
      ) : sessions.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">Sin conteos registrados.</p>
      ) : sessions.map((session) => {
        const statusMeta = getCountSessionStatusMeta(session);
        return (
          <button
            key={session.id}
            type="button"
            onClick={() => onOpen(session)}
            className="w-full p-4 text-left hover:bg-slate-50"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="font-semibold text-slate-900">{session.name || `Conteo #${session.id}`}</p>
                <p className="text-xs text-slate-500">
                  {formatDateTime(session.countedAt)} - {getUserDisplayName(session.responsibleUser)}
                </p>
              </div>
              <span className={`inline-flex w-fit items-center rounded-full border px-2 py-1 text-xs font-semibold ${statusMeta.className}`}>
                {statusMeta.label}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-600">
              <span>{session.itemCount || 0} filas</span>
              {session.items?.some(item => item.trackingMode === 'pack_warehouse') && <span>Diferencia: {session.totalVariancePacks || 0} packs</span>}
              <span className="text-red-700">Falta {formatKg(session.missingKg)}</span>
              <span className="text-emerald-700">Sobra {formatKg(session.excessKg)}</span>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const InventoryEmailModal = ({
  branchId,
  search,
  status,
  selectedCategoryIds = [],
  categories = [],
  totalRows,
  kpis = {},
  onClose,
}) => {
  const [recipients, setRecipients] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [sending, setSending] = useState(false);

  const categoryLabels = useMemo(() => {
    if (!selectedCategoryIds.length) return ['Todas'];
    const categoryMap = new Map((categories || []).map((category) => [String(category.id), getCategoryLabel(category)]));
    return selectedCategoryIds.map((id) => categoryMap.get(String(id)) || `Categoria ${id}`);
  }, [categories, selectedCategoryIds]);

  const loadSuggestions = useCallback(async (query = '') => {
    if (!branchId) return [];
    const response = await getInventoryEmailSuggestions(branchId, {
      search: query,
      limit: 20,
    });
    const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
    return list.map(toEmailOption).filter(Boolean);
  }, [branchId]);

  useEffect(() => {
    let active = true;
    setLoadingSuggestions(true);
    loadSuggestions('')
      .then((items) => {
        if (active) setSuggestions(items);
      })
      .catch(() => {
        if (active) setSuggestions([]);
      })
      .finally(() => {
        if (active) setLoadingSuggestions(false);
      });
    return () => {
      active = false;
    };
  }, [loadSuggestions]);

  const handleSend = async () => {
    const emails = recipients
      .map((entry) => normalizeEmail(entry.email || entry.id))
      .filter(isValidEmail);
    if (!emails.length) {
      notify('Agrega al menos un correo válido.');
      return;
    }
    setSending(true);
    try {
      await sendInventoryEmail(branchId, {
        recipients: emails,
        filters: {
          search,
          status,
          categoryIds: selectedCategoryIds,
        },
      });
      notify('Correo de inventario enviado.');
      onClose();
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo enviar el correo de inventario.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1600] bg-slate-950/50 px-3 py-4 md:pl-20 md:pr-6 overflow-y-auto">
      <div className="mx-auto max-w-2xl rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Enviar reporte</p>
            <h2 className="text-xl font-semibold text-slate-900">Inventario en PDF</h2>
            <p className="text-sm text-slate-600">Se enviará con los filtros actuales de sucursal y categorías.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Cerrar"
          >
            <FiX />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
            <DetailLine label="Sucursal" value={`Sucursal ${branchId}`} />
            <DetailLine label="Estado" value={INVENTORY_STATUS_LABELS[status] || status || 'Todos'} />
            <DetailLine label="Categorias" value={categoryLabels.join(', ')} className="break-words" />
            <DetailLine label="Busqueda" value={search || 'Sin busqueda'} className="break-words" />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filas</p>
              <p className="text-xl font-bold text-slate-900">{totalRows || 0}</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Disponible</p>
              <p className="text-xl font-bold text-slate-900">{formatKg(kpis.availableKg)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mayorista</p>
              <p className="text-xl font-bold text-emerald-700">{formatInventoryCurrency(kpis.wholesaleEstimateAmount)}</p>
            </div>
          </div>

          <div>
            <SearchMultiSelect
              label="Destinatarios"
              placeholder={loadingSuggestions ? 'Cargando sugerencias...' : 'Buscar o escribir correo'}
              value={recipients}
              options={suggestions}
              onChange={setRecipients}
              onSearch={loadSuggestions}
              getOptionLabel={(option) => option.label || option.email || option.id}
              allowCreate
              createLabel={(query) => `Enviar a ${query}`}
              onCreateOption={(query) => {
                if (!isValidEmail(query)) {
                  notify('Ingresa un correo válido.');
                  return null;
                }
                return toEmailOption(query);
              }}
            />
            <p className="mt-2 text-xs text-slate-500">
              Las sugerencias combinan correos usados antes para inventario y usuarios de esta sucursal.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:bg-slate-400"
          >
            <FiSend /> {sending ? 'Enviando...' : 'Enviar PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MissingConversionAction = ({ disabled = false, onClick }) => {
  const content = (
    <>
      <FiAlertTriangle /> Sin conversion
    </>
  );
  if (disabled) {
    return <span className="inline-flex items-center gap-1 text-amber-700">{content}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-end gap-1 text-amber-700 underline decoration-amber-300 underline-offset-2 hover:text-amber-900"
    >
      {content}
    </button>
  );
};

const InventoryConversionModal = ({
  branchId,
  row,
  metricUnit,
  onClose,
  onSaved,
}) => {
  const selectedOption = findUnitOption(row, metricUnit);
  const currentBaseUnit = getInventoryBaseUnit(row);
  const selectedUnit = metricUnit || selectedOption?.metricUnit || getDefaultUnit(row);
  const initialFactor = parsePositiveConversionValue(
    selectedOption?.baseQuantityPerUnit || selectedOption?.effectiveBaseQuantityPerUnit
  );
  const [baseUnit, setBaseUnit] = useState(getSuggestedInventoryBaseUnit(row));
  const [factor, setFactor] = useState(initialFactor ? String(initialFactor) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const normalizedBaseUnit = String(baseUnit || '').trim();
  const direct = isDirectInventoryEquivalence(normalizedBaseUnit, selectedUnit, {
    categoryIsResaleOnly: row?.categoryIsResaleOnly,
  });
  const selectedPriceId = selectedOption?.varietyPriceId || selectedOption?.id;
  const baseUnitChanged = toInventoryUnitKey(normalizedBaseUnit) !== toInventoryUnitKey(currentBaseUnit);
  const parsedFactor = parsePositiveConversionValue(factor);

  const handleSave = async () => {
    if (!row?.varietyId) {
      setError('No se pudo identificar la variedad.');
      return;
    }
    if (!normalizedBaseUnit) {
      setError('Ingresa la unidad base de inventario.');
      return;
    }
    if (!direct && !selectedPriceId) {
      setError('No se encontro un formato de precio para esta unidad.');
      return;
    }
    if (!direct && !parsedFactor) {
      setError('Ingresa una equivalencia mayor a 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (baseUnitChanged) {
        await updateVarietyInventorySettings(branchId, row.varietyId, {
          inventoryBaseUnit: normalizedBaseUnit,
        });
      }
      if (!direct) {
        await updateVarietyPriceEquivalence(branchId, selectedPriceId, {
          baseQuantityPerUnit: parsedFactor,
        });
      }
      notify('Conversion guardada.');
      await onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'No se pudo guardar la conversion.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1700] bg-slate-950/60 px-3 py-4 md:pl-20 md:pr-6 overflow-y-auto">
      <div className="mx-auto max-w-xl rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conversion pendiente</p>
            <h2 className="text-lg font-semibold text-slate-900">{getRowLabel(row)}</h2>
            <p className="mt-1 text-sm text-slate-600">
              Configura como convertir {selectedUnit || 'esta unidad'} a la unidad base de inventario.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Cerrar"
          >
            <FiX />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
            <DetailLine label="Categoria" value={row?.categoryName || '-'} />
            <DetailLine label="Unidad actual" value={currentBaseUnit || '-'} />
            <DetailLine label="Unidad seleccionada" value={selectedUnit || '-'} />
            <DetailLine label="Tipo" value={row?.categoryIsResaleOnly ? 'Reventa sin conversion' : 'Inventario convertible'} />
          </div>

          {row?.categoryIsResaleOnly && toInventoryUnitKey(currentBaseUnit) !== 'unidad' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Esta categoria es de reventa. Usar unidad como base evita convertir estos productos a kg.
            </div>
          )}

          <label className="block text-sm font-semibold text-slate-700">
            Unidad base de inventario
            <input
              value={baseUnit}
              onChange={(event) => setBaseUnit(event.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="kg, unidad, caja"
            />
          </label>

          {direct ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              Con esta unidad base, {selectedUnit || 'la unidad seleccionada'} se descuenta directo del inventario.
            </div>
          ) : (
            <label className="block text-sm font-semibold text-slate-700">
              Equivalencia
              <div className="mt-1 grid grid-cols-[auto_1fr_auto] items-center gap-2">
                <span className="text-sm text-slate-600">1 {selectedUnit || 'unidad'} =</span>
                <input
                  type="number"
                  min="0"
                  step="0.000001"
                  value={factor}
                  onChange={(event) => setFactor(event.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-right text-sm"
                />
                <span className="text-sm text-slate-600">{normalizedBaseUnit || 'base'}</span>
              </div>
              <span className="mt-1 block text-xs font-normal text-slate-500">
                Ejemplo: si 1 caja contiene 12 unidades, ingresa 12.
              </span>
            </label>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:bg-slate-400"
          >
            <FiSave /> {saving ? 'Guardando...' : 'Guardar conversion'}
          </button>
        </div>
      </div>
    </div>
  );
};

const InventoryCountWorkspace = ({
  branchId,
  sessionId,
  user,
  categoryOptions = [],
  initialCategoryIds = [],
  initialRowKeys = [],
  onClose,
  onSaved,
}) => {
  const [inventoryRows, setInventoryRows] = useState([]);
  const [session, setSession] = useState(null);
  const [entries, setEntries] = useState({});
  const [meta, setMeta] = useState({
    name: `Conteo ${new Date().toLocaleDateString('es-CL')}`,
    countedAt: toDateTimeLocalValue(),
    note: '',
  });
  const [search, setSearch] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() =>
    sessionId ? [] : (initialCategoryIds || []).map(String)
  );
  const [expandedVarietyId, setExpandedVarietyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [conversionSetup, setConversionSetup] = useState(null);
  const { searchMode } = useProductSearchSettings();

  const rowsById = useMemo(
    () => new Map(inventoryRows.map((row) => [getRowInventoryKey(row), row])),
    [inventoryRows]
  );
  const readOnly = session?.status === 'completed' || !hasPermission(user, 'stock.update');
  const responsible = session?.responsibleUser || user;

  const loadWorkspace = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const [allRows, countResponse] = await Promise.all([
        fetchAllInventoryRows(branchId),
        sessionId ? getInventoryCountSession(branchId, sessionId) : Promise.resolve(null),
      ]);
      setInventoryRows(!sessionId && initialRowKeys.length ? allRows.filter(row => initialRowKeys.includes(getRowInventoryKey(row))) : allRows);
      const loadedSession = countResponse?.session || null;
      setSession(loadedSession);
      if (loadedSession) {
        setSelectedCategoryIds([]);
        const nextEntries = {};
        (loadedSession.items || []).forEach((item) => {
          const inputUnitKey = toInventoryUnitKey(item.inputMetricUnit);
          const row = allRows.find((entry) =>
            Number(entry.varietyId) === Number(item.varietyId)
            && (!item.varietyPriceId || Number(entry.varietyPriceId) === Number(item.varietyPriceId))
            && (
              !entry.usesUnitInventory
              || toInventoryUnitKey(entry.inventoryMetricUnitKey || entry.inventoryMetricUnit) === inputUnitKey
            )
          ) || allRows.find((entry) => Number(entry.varietyId) === Number(item.varietyId));
          const rowKey = getRowInventoryKey(row);
          if (!rowKey) return;
          nextEntries[rowKey] = {
            quantity: item.inputQuantity ?? '',
            metricUnit: item.inputMetricUnit || getDefaultUnit(row),
            note: item.note || '',
          };
        });
        setEntries(nextEntries);
        setMeta({
          name: loadedSession.name || `Conteo #${loadedSession.id}`,
          countedAt: toDateTimeLocalValue(loadedSession.countedAt),
          note: loadedSession.note || '',
        });
      }
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo cargar el conteo.');
    } finally {
      setLoading(false);
    }
  }, [branchId, sessionId, initialRowKeys]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const categoryScopedRows = useMemo(() => {
    const selectedSet = new Set((selectedCategoryIds || []).map(String));
    if (!selectedSet.size) return inventoryRows;
    return inventoryRows.filter((row) => selectedSet.has(getRowCategoryId(row)));
  }, [inventoryRows, selectedCategoryIds]);

  const scopedRowKeys = useMemo(
    () => new Set(categoryScopedRows.map((row) => getRowInventoryKey(row))),
    [categoryScopedRows]
  );
  const searchSuggestionProducts = useMemo(
    () => categoryScopedRows.map((row) => ({
      productName: row.productName,
      Varieties: [{ varietyName: row.varietyName }],
    })),
    [categoryScopedRows]
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return categoryScopedRows;
    return categoryScopedRows.filter((row) =>
      fieldsMatchProductSearchTerms([row.productName, row.varietyName], search, { searchMode })
    );
  }, [categoryScopedRows, search, searchMode]);

  const updateEntry = (row, patch) => {
    if (readOnly) return;
    const rowKey = getRowInventoryKey(row);
    setEntries((prev) => ({
      ...prev,
      [rowKey]: {
        quantity: '',
        metricUnit: getDefaultUnit(row),
        note: '',
        ...(prev[rowKey] || {}),
        ...patch,
      },
    }));
  };

  const openConversionSetup = (row, metricUnit) => {
    if (readOnly) return;
    if (!row) {
      notify('No se pudo identificar la fila para configurar la conversion.');
      return;
    }
    setConversionSetup({ row, metricUnit: metricUnit || getDefaultUnit(row) });
  };

  const handleConversionSaved = async () => {
    await loadWorkspace();
    onSaved?.();
  };

  const serializeItems = () => buildEntryItems({
    entries,
    rowsById,
    requirePositive: false,
    allowedRowKeys: scopedRowKeys,
  });

  const persistDraft = async ({ showNotification = true } = {}) => {
    const items = serializeItems();
    if (items.some(item => !Number.isFinite(item.quantity) || item.quantity < 0 || (item.trackingMode === 'pack_warehouse' && !Number.isSafeInteger(item.quantity)))) {
      notify('Los conteos de bodega requieren cantidades enteras de packs, incluyendo cero.');
      return null;
    }
    const missingConversion = findMissingConversion({ items, rowsById });
    if (missingConversion) {
      openConversionSetup(missingConversion.row, missingConversion.item.metricUnit);
      notify(`Configura la conversion para ${missingConversion.item.metricUnit}.`);
      return null;
    }
    const payload = {
      name: meta.name,
      countedAt: toIsoDateTime(meta.countedAt),
      note: meta.note,
      items,
    };
    setSaving(true);
    try {
      const response = session?.id
        ? await updateInventoryCount(branchId, session.id, payload)
        : await createInventoryCount(branchId, payload);
      setSession(response.session);
      if (showNotification) notify('Borrador de conteo guardado.');
      onSaved?.();
      return response.session;
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo guardar el conteo.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (readOnly) return;
    await persistDraft();
  };

  const handleFinalize = async () => {
    if (readOnly) return;
    const items = serializeItems();
    if (!items.length) {
      notify('Agrega al menos una fila con cantidad contada.');
      return;
    }
    setFinalizing(true);
    try {
      const draft = await persistDraft({ showNotification: false });
      if (!draft?.id) return;
      await finalizeInventoryCount(branchId, draft.id);
      notify('Conteo finalizado.');
      onSaved?.();
      onClose();
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo finalizar el conteo.');
    } finally {
      setFinalizing(false);
    }
  };

  const enteredCount = serializeItems().length;

  return (
    <div className="fixed inset-0 z-[1600] bg-slate-950/50 px-3 py-4 md:pl-20 md:pr-6 overflow-y-auto">
      <div className="mx-auto max-w-7xl bg-white rounded-lg shadow-xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lista de conteo</p>
            <h2 className="text-xl font-semibold text-slate-900">
              {session?.id ? meta.name || `Conteo #${session.id}` : 'Nuevo conteo'}
            </h2>
            <p className="text-sm text-slate-600">Responsable: {getUserDisplayName(responsible)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <FiX /> Cerrar
            </button>
            {!readOnly && (
              <>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={saving || finalizing}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <FiSave /> {saving ? 'Guardando...' : 'Guardar borrador'}
                </button>
                <button
                  type="button"
                  onClick={handleFinalize}
                  disabled={saving || finalizing}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:bg-slate-400"
                >
                  <FiCheck /> {finalizing ? 'Finalizando...' : 'Finalizar conteo'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="p-5 border-b border-slate-200 grid grid-cols-1 lg:grid-cols-[1fr_220px_1fr] gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Nombre</label>
            <input
              value={meta.name}
              onChange={(event) => setMeta((prev) => ({ ...prev, name: event.target.value }))}
              disabled={readOnly}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-100"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Fecha</label>
            <input
              type="datetime-local"
              value={meta.countedAt}
              onChange={(event) => setMeta((prev) => ({ ...prev, countedAt: event.target.value }))}
              disabled={readOnly}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-100"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Nota</label>
            <input
              value={meta.note}
              onChange={(event) => setMeta((prev) => ({ ...prev, note: event.target.value }))}
              disabled={readOnly}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-100"
            />
          </div>
        </div>

        <div className="p-5 border-b border-slate-200 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_320px_auto] lg:items-start">
          <label className="relative w-full md:max-w-md">
            <FiSearch className="absolute left-3 top-3 text-slate-400" />
            <ProductSearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              products={searchSuggestionProducts}
              className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2"
              placeholder="Buscar producto o variedad"
            />
          </label>
          <CategoryMultiSelect
            categories={categoryOptions}
            selectedCategoryIds={selectedCategoryIds}
            onChange={setSelectedCategoryIds}
            disabled={readOnly}
            label=""
            placeholder="Filtrar categorias"
          />
          <div className="text-sm text-slate-600 lg:text-right">
            {enteredCount} filas con conteo / {categoryScopedRows.length} variedades
          </div>
        </div>

        <div className="md:hidden divide-y divide-slate-200">
          {loading ? (
            <div className="px-4 py-8 text-center">
              <InlineLoading label="Cargando inventario" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              No hay productos para mostrar.
            </div>
          ) : filteredRows.map((row, index) => {
            const rowKey = getRowInventoryKey(row);
            const entry = entries[rowKey] || {
              quantity: '',
              metricUnit: getDefaultUnit(row),
              note: '',
            };
            const entered = isQuantityEntered(entry.quantity);
            const kgPreview = entered ? getInventoryKgPreview(row, entry.quantity, entry.metricUnit) : null;
            const variance = entered ? getInventoryVariancePreview(row, entry.quantity, entry.metricUnit) : null;
            const unitOptions = row.unitOptions?.length
              ? row.unitOptions
              : [{ metricUnit: 'kg', metricUnitKey: 'kg' }];
            const isExpanded = String(expandedVarietyId) === rowKey;
            return (
              <article key={rowKey} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}>
                <button
                  type="button"
                  onClick={() => setExpandedVarietyId(isExpanded ? null : rowKey)}
                  className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
                >
                  <ProductIdentity row={row} imageSize="h-12 w-12" />
                  <div className="flex shrink-0 items-center gap-2 text-right">
                    <div>
                      <p className="text-[11px] text-slate-500">Esperado</p>
                      <p className="text-sm font-semibold text-slate-900">{formatInventoryMeasure(row, (row.availableQuantity ?? row.availableKg))}</p>
                    </div>
                    {isExpanded ? <FiChevronUp className="text-slate-400" /> : <FiChevronDown className="text-slate-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <DetailLine label="Unidades" value={formatStockedUnits(row.stockedUnits)} />
                      <DetailLine label={row.usesUnitInventory ? 'Cantidad' : 'Equiv. kg'} value={
                        entered && kgPreview === null ? (
                          <MissingConversionAction
                            disabled={readOnly}
                            onClick={() => openConversionSetup(row, entry.metricUnit)}
                          />
                        ) : entered ? formatInventoryMeasure(row, kgPreview) : '-'
                      } />
                      <DetailLine
                        label="Falta / sobra"
                        className={Number(variance) < 0 ? 'font-semibold text-red-700' : 'font-semibold text-emerald-700'}
                        value={entered && variance !== null ? formatInventoryMeasure(row, variance) : '-'}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Conteo
                        <input
                          type="number"
                          min="0"
                          step={row.trackingMode === 'pack_warehouse' ? '1' : '0.001'}
                          value={entry.quantity}
                          onChange={(event) => updateEntry(row, { quantity: event.target.value })}
                          disabled={readOnly}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-right text-sm disabled:bg-slate-100"
                        />
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Unidad
                        <select
                          value={entry.metricUnit}
                          onChange={(event) => updateEntry(row, { metricUnit: event.target.value })}
                          disabled={readOnly}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm disabled:bg-slate-100"
                        >
                          {unitOptions.map((option) => (
                            <option key={option.metricUnitKey || option.metricUnit} value={option.metricUnit}>
                              {option.metricUnit}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Nota
                        <input
                          value={entry.note}
                          onChange={(event) => updateEntry(row, { note: event.target.value })}
                          disabled={readOnly}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm disabled:bg-slate-100"
                        />
                      </label>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Producto</th>
                <th className="px-4 py-3 text-right">Esperado</th>
                <th className="px-4 py-3 text-left">Unidades</th>
                <th className="px-4 py-3 text-right">Conteo</th>
                <th className="px-4 py-3 text-left">Unidad</th>
                <th className="px-4 py-3 text-right">Equiv.</th>
                <th className="px-4 py-3 text-right">Falta / sobra</th>
                <th className="px-4 py-3 text-left">Nota</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center">
                    <InlineLoading label="Cargando inventario" />
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                    No hay productos para mostrar.
                  </td>
                </tr>
              ) : filteredRows.map((row) => {
                const rowKey = getRowInventoryKey(row);
                const entry = entries[rowKey] || {
                  quantity: '',
                  metricUnit: getDefaultUnit(row),
                  note: '',
                };
                const entered = isQuantityEntered(entry.quantity);
                const kgPreview = entered ? getInventoryKgPreview(row, entry.quantity, entry.metricUnit) : null;
                const variance = entered ? getInventoryVariancePreview(row, entry.quantity, entry.metricUnit) : null;
                const unitOptions = row.unitOptions?.length
                  ? row.unitOptions
                  : [{ metricUnit: 'kg', metricUnitKey: 'kg' }];
                return (
                  <tr key={rowKey} className="odd:bg-white even:bg-slate-50/70 hover:bg-slate-100">
                    <td className="px-4 py-3 min-w-56">
                      <ProductIdentity row={row} />
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{formatInventoryMeasure(row, (row.availableQuantity ?? row.availableKg))}</td>
                    <td className="px-4 py-3 text-slate-700 min-w-52">{formatStockedUnits(row.stockedUnits)}</td>
                    <td className="px-4 py-3 min-w-32">
                      <input
                        type="number"
                        min="0"
                        step={row.trackingMode === 'pack_warehouse' ? '1' : '0.001'}
                        value={entry.quantity}
                        onChange={(event) => updateEntry(row, { quantity: event.target.value })}
                        disabled={readOnly}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1 text-right disabled:bg-slate-100"
                      />
                    </td>
                    <td className="px-4 py-3 min-w-36">
                      <select
                        value={entry.metricUnit}
                        onChange={(event) => updateEntry(row, { metricUnit: event.target.value })}
                        disabled={readOnly}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1 disabled:bg-slate-100"
                      >
                        {unitOptions.map((option) => (
                          <option key={option.metricUnitKey || option.metricUnit} value={option.metricUnit}>
                            {option.metricUnit}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {entered && kgPreview === null ? (
                        <MissingConversionAction
                          disabled={readOnly}
                          onClick={() => openConversionSetup(row, entry.metricUnit)}
                        />
                      ) : entered ? formatInventoryMeasure(row, kgPreview) : '-'}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${Number(variance) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      {entered && variance !== null ? formatInventoryMeasure(row, variance) : '-'}
                    </td>
                    <td className="px-4 py-3 min-w-56">
                      <input
                        value={entry.note}
                        onChange={(event) => updateEntry(row, { note: event.target.value })}
                        disabled={readOnly}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1 disabled:bg-slate-100"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {conversionSetup && (
          <InventoryConversionModal
            branchId={branchId}
            row={conversionSetup.row}
            metricUnit={conversionSetup.metricUnit}
            onClose={() => setConversionSetup(null)}
            onSaved={handleConversionSaved}
          />
        )}
      </div>
    </div>
  );
};

const BulkMermaWorkspace = ({ branchId, onClose, onSaved }) => {
  const [inventoryRows, setInventoryRows] = useState([]);
  const [entries, setEntries] = useState({});
  const [search, setSearch] = useState('');
  const [expandedVarietyId, setExpandedVarietyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [conversionSetup, setConversionSetup] = useState(null);
  const { searchMode } = useProductSearchSettings();

  const rowsById = useMemo(
    () => new Map(inventoryRows.map((row) => [getRowInventoryKey(row), row])),
    [inventoryRows]
  );
  const searchSuggestionProducts = useMemo(
    () => inventoryRows.map((row) => ({
      productName: row.productName,
      Varieties: [{ varietyName: row.varietyName }],
    })),
    [inventoryRows]
  );

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      setInventoryRows((await fetchAllInventoryRows(branchId)).filter(row => row.trackingMode !== 'pack_warehouse'));
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo cargar inventario.');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (branchId) loadRows();
  }, [branchId, loadRows]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return inventoryRows;
    return inventoryRows.filter((row) =>
      fieldsMatchProductSearchTerms([row.productName, row.varietyName], search, { searchMode })
    );
  }, [inventoryRows, search, searchMode]);

  const updateEntry = (row, patch) => {
    const rowKey = getRowInventoryKey(row);
    setEntries((prev) => ({
      ...prev,
      [rowKey]: {
        quantity: '',
        metricUnit: getDefaultUnit(row),
        reason: 'Merma',
        note: '',
        ...(prev[rowKey] || {}),
        ...patch,
      },
    }));
  };

  const openConversionSetup = (row, metricUnit) => {
    if (!row) {
      notify('No se pudo identificar la fila para configurar la conversion.');
      return;
    }
    setConversionSetup({ row, metricUnit: metricUnit || getDefaultUnit(row) });
  };

  const handleConversionSaved = async () => {
    await loadRows();
    onSaved?.();
  };

  const serializeItems = () => buildEntryItems({
    entries,
    rowsById,
    requirePositive: true,
  });

  const handleSave = async () => {
    const items = serializeItems();
    if (!items.length) {
      notify('Agrega al menos una fila con merma.');
      return;
    }
    const missingConversion = findMissingConversion({ items, rowsById });
    if (missingConversion) {
      openConversionSetup(missingConversion.row, missingConversion.item.metricUnit);
      notify(`Configura la conversion para ${missingConversion.item.metricUnit}.`);
      return;
    }
    setSaving(true);
    try {
      await createInventoryMerma(branchId, { items });
      notify('Merma registrada.');
      onSaved?.();
      onClose();
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo registrar merma.');
    } finally {
      setSaving(false);
    }
  };

  const enteredCount = serializeItems().length;

  return (
    <div className="fixed inset-0 z-[1600] bg-slate-950/50 px-3 py-4 md:pl-20 md:pr-6 overflow-y-auto">
      <div className="mx-auto max-w-7xl bg-white rounded-lg shadow-xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Merma masiva</p>
            <h2 className="text-xl font-semibold text-slate-900">Registrar perdidas</h2>
            <p className="text-sm text-slate-600">{enteredCount} filas listas para guardar</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <FiX /> Cerrar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:bg-red-300"
            >
              <FiTrendingDown /> {saving ? 'Guardando...' : 'Guardar merma'}
            </button>
          </div>
        </div>

        <div className="p-5 border-b border-slate-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <label className="relative w-full md:max-w-md">
            <FiSearch className="absolute left-3 top-3 text-slate-400" />
            <ProductSearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              products={searchSuggestionProducts}
              className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2"
              placeholder="Buscar producto o variedad"
            />
          </label>
          <div className="text-sm text-slate-600">{inventoryRows.length} variedades</div>
        </div>

        <div className="md:hidden divide-y divide-slate-200">
          {loading ? (
            <div className="px-4 py-8 text-center">
              <InlineLoading label="Cargando inventario" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              No hay productos para mostrar.
            </div>
          ) : filteredRows.map((row, index) => {
            const rowKey = getRowInventoryKey(row);
            const entry = entries[rowKey] || {
              quantity: '',
              metricUnit: getDefaultUnit(row),
              reason: 'Merma',
              note: '',
            };
            const entered = isQuantityEntered(entry.quantity);
            const kgPreview = entered ? getInventoryKgPreview(row, entry.quantity, entry.metricUnit) : null;
            const unitOptions = row.unitOptions?.length
              ? row.unitOptions
              : [{ metricUnit: 'kg', metricUnitKey: 'kg' }];
            const isExpanded = String(expandedVarietyId) === rowKey;
            return (
              <article key={rowKey} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}>
                <button
                  type="button"
                  onClick={() => setExpandedVarietyId(isExpanded ? null : rowKey)}
                  className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
                >
                  <ProductIdentity row={row} imageSize="h-12 w-12" />
                  <div className="flex shrink-0 items-center gap-2 text-right">
                    <div>
                      <p className="text-[11px] text-slate-500">Disponible</p>
                      <p className="text-sm font-semibold text-slate-900">{formatInventoryMeasure(row, (row.availableQuantity ?? row.availableKg))}</p>
                    </div>
                    {isExpanded ? <FiChevronUp className="text-slate-400" /> : <FiChevronDown className="text-slate-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <DetailLine label={row.usesUnitInventory ? 'Cantidad' : 'Equiv. kg'} value={
                        entered && kgPreview === null ? (
                          <MissingConversionAction
                            onClick={() => openConversionSetup(row, entry.metricUnit)}
                          />
                        ) : entered ? formatInventoryMeasure(row, kgPreview) : '-'
                      } />
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Merma
                        <input
                          type="number"
                          min="0"
                          step={row.trackingMode === 'pack_warehouse' ? '1' : '0.001'}
                          value={entry.quantity}
                          onChange={(event) => updateEntry(row, { quantity: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-right text-sm"
                        />
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Unidad
                        <select
                          value={entry.metricUnit}
                          onChange={(event) => updateEntry(row, { metricUnit: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm"
                        >
                          {unitOptions.map((option) => (
                            <option key={option.metricUnitKey || option.metricUnit} value={option.metricUnit}>
                              {option.metricUnit}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Motivo
                        <input
                          value={entry.reason}
                          onChange={(event) => updateEntry(row, { reason: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm"
                        />
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Nota
                        <input
                          value={entry.note}
                          onChange={(event) => updateEntry(row, { note: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm"
                        />
                      </label>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Producto</th>
                <th className="px-4 py-3 text-right">Disponible</th>
                <th className="px-4 py-3 text-right">Merma</th>
                <th className="px-4 py-3 text-left">Unidad</th>
                <th className="px-4 py-3 text-right">Equiv.</th>
                <th className="px-4 py-3 text-left">Motivo</th>
                <th className="px-4 py-3 text-left">Nota</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <InlineLoading label="Cargando inventario" />
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                    No hay productos para mostrar.
                  </td>
                </tr>
              ) : filteredRows.map((row) => {
                const rowKey = getRowInventoryKey(row);
                const entry = entries[rowKey] || {
                  quantity: '',
                  metricUnit: getDefaultUnit(row),
                  reason: 'Merma',
                  note: '',
                };
                const entered = isQuantityEntered(entry.quantity);
                const kgPreview = entered ? getInventoryKgPreview(row, entry.quantity, entry.metricUnit) : null;
                const unitOptions = row.unitOptions?.length
                  ? row.unitOptions
                  : [{ metricUnit: 'kg', metricUnitKey: 'kg' }];
                return (
                  <tr key={rowKey} className="odd:bg-white even:bg-slate-50/70 hover:bg-slate-100">
                    <td className="px-4 py-3 min-w-56">
                      <ProductIdentity row={row} />
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{formatInventoryMeasure(row, (row.availableQuantity ?? row.availableKg))}</td>
                    <td className="px-4 py-3 min-w-32">
                      <input
                        type="number"
                        min="0"
                        step={row.trackingMode === 'pack_warehouse' ? '1' : '0.001'}
                        value={entry.quantity}
                        onChange={(event) => updateEntry(row, { quantity: event.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1 text-right"
                      />
                    </td>
                    <td className="px-4 py-3 min-w-36">
                      <select
                        value={entry.metricUnit}
                        onChange={(event) => updateEntry(row, { metricUnit: event.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1"
                      >
                        {unitOptions.map((option) => (
                          <option key={option.metricUnitKey || option.metricUnit} value={option.metricUnit}>
                            {option.metricUnit}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {entered && kgPreview === null ? (
                        <MissingConversionAction
                          onClick={() => openConversionSetup(row, entry.metricUnit)}
                        />
                      ) : entered ? formatInventoryMeasure(row, kgPreview) : '-'}
                    </td>
                    <td className="px-4 py-3 min-w-44">
                      <input
                        value={entry.reason}
                        onChange={(event) => updateEntry(row, { reason: event.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1"
                      />
                    </td>
                    <td className="px-4 py-3 min-w-56">
                      <input
                        value={entry.note}
                        onChange={(event) => updateEntry(row, { note: event.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {conversionSetup && (
          <InventoryConversionModal
            branchId={branchId}
            row={conversionSetup.row}
            metricUnit={conversionSetup.metricUnit}
            onClose={() => setConversionSetup(null)}
            onSaved={handleConversionSaved}
          />
        )}
      </div>
    </div>
  );
};

const InventoryBranchPage = ({ branchId, user }) => {
  const { searchMode } = useProductSearchSettings();
  const canUpdateStock = hasPermission(user, 'stock.update');
  const [warehouseOperation, setWarehouseOperation] = useState(null);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [countRowKeys, setCountRowKeys] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [status, setStatus] = useState('all');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');
  const [categoryAttempt, setCategoryAttempt] = useState(0);
  const inventory = useInventoryCategory(branchId, selectedCategoryId);
  const { loading, refresh: fetchSummary } = inventory;
  const selectedCategoryIds = useMemo(() => selectedCategoryId ? [selectedCategoryId] : [], [selectedCategoryId]);
  const rows = useMemo(() => inventory.rows.filter(row =>
    (!search || fieldsMatchProductSearchTerms([row.productName, row.varietyName], search, { searchMode }))
    && (status === 'all' || row.status === status)), [inventory.rows, search, searchMode, status]);
  const totalRows = rows.length;
  const kpis = useMemo(() => rows.reduce((totals, row) => {
    Object.entries(row.summaryTotals || {}).forEach(([key, value]) => { totals[key] = (totals[key] || 0) + Number(value); });
    return totals;
  }, {}), [rows]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [countSessions, setCountSessions] = useState([]);
  const [countSessionsLoading, setCountSessionsLoading] = useState(false);
  const [countWorkspaceSessionId, setCountWorkspaceSessionId] = useState(null);
  const [countWorkspaceOpen, setCountWorkspaceOpen] = useState(false);
  const [mermaWorkspaceOpen, setMermaWorkspaceOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [conversionSetup, setConversionSetup] = useState(null);

  const searchSuggestionProducts = useMemo(
    () => inventory.rows.map(row => ({ productName: row.productName, Varieties: [{ varietyName: row.varietyName }] })),
    [inventory.rows]
  );
  const categories = categoryOptions;

  usePurchaseEvents(branchId, event => {
    if (event?.branchId && String(event.branchId) !== String(branchId)) return;
    if (event?.companyId && String(event.companyId) !== String(user?.selectedCompanyId)) return;
    if (['inventory-updated', 'purchase-created', 'purchase-updated', 'purchase-reopened'].includes(event?.type)) {
      fetchSummary(); setRefreshKey(value => value + 1);
    }
  });

  const fetchCountSessions = useCallback(async () => {
    if (!branchId) return;
    setCountSessionsLoading(true);
    try {
      const response = await getInventoryCountSessions(branchId, {
        pageSize: 8,
        sortBy: 'countedAt',
        sortOrder: 'desc',
      });
      const normalized = normalizePaginatedResponse(response);
      setCountSessions(normalized.data || []);
    } catch (error) {
      setCountSessions([]);
    } finally {
      setCountSessionsLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    setSelectedRow(null); setWarehouseOperation(null); setCountWorkspaceOpen(false); setMermaWorkspaceOpen(false); setConversionSetup(null); setEmailModalOpen(false);
  }, [branchId, user?.selectedCompanyId]);

  useEffect(() => {
    fetchCountSessions();
  }, [fetchCountSessions]);

  useEffect(() => {
    let active = true;
    setCategoriesLoading(true); setCategoriesError('');
    getCategories({ branchId }).then(data => {
      if (!active) return;
      const options = (Array.isArray(data) ? data : []).filter(category => category.tracksInventory);
      setCategoryOptions(options);
      setSelectedCategoryId(previous => options.some(category => String(category.id) === previous)
        ? previous : options.length ? String(options[0].id) : '');
    }).catch(() => {
      if (active) setCategoriesError('No se pudieron cargar las categorías.');
    }).finally(() => { if (active) setCategoriesLoading(false); });
    return () => { active = false; };
  }, [branchId, categoryAttempt]);

  const refreshInventoryViews = async () => {
    setRefreshKey(value => value + 1);
    await Promise.all([
      fetchSummary(),
      fetchCountSessions(),
    ]);
  };

  const openInventoryConversionSetup = (row, metricUnit) => {
    if (!canUpdateStock) return;
    if (!row) {
      notify('No se pudo identificar la fila para configurar la conversion.');
      return;
    }
    setConversionSetup({ row, metricUnit: metricUnit || getDefaultUnit(row) });
  };

  const handleInventoryConversionSaved = async () => {
    await refreshInventoryViews();
  };

  const openNewCount = (selected = []) => {
    setCountRowKeys(selected.map(getRowInventoryKey));
    setCountWorkspaceSessionId(null);
    setCountWorkspaceOpen(true);
  };

  const openExistingCount = (session) => {
    setCountRowKeys([]);
    setCountWorkspaceSessionId(session.id);
    setCountWorkspaceOpen(true);
  };

  return (
    <div className="inventory-page min-h-screen bg-slate-100 p-3 md:p-6">
      <InventoryWorkspace key={`workspace:${user?.selectedCompanyId}:${branchId}`} branchId={branchId} user={user}
        rows={rows} loading={loading || categoriesLoading} error={categoriesError || inventory.error} totalRows={totalRows}
        progress={selectedCategoryId ? { loaded: inventory.loaded, total: inventory.total } : null}
        onRetry={categoriesError ? () => setCategoryAttempt(value => value + 1) : inventory.retry}
        emptyMessage={!categoriesLoading && !categories.length ? 'No hay categorías con inventario habilitado.' : undefined}
        filtersKey={`${search}:${selectedCategoryId}:${status}`} refreshKey={refreshKey} selectionResetKey={selectionResetKey}
        onRefresh={refreshInventoryViews} onWithdraw={selected => setWarehouseOperation({ mode: 'withdrawal', rows: selected })}
        onMerma={() => setWarehouseOperation({ mode: 'merma', rows: [] })} onCount={openNewCount}
        onMovements={setSelectedRow} onEmail={() => setEmailModalOpen(true)}
        countSessions={<CountSessionsPanel sessions={countSessions} loading={countSessionsLoading} onOpen={openExistingCount} />}
        filters={<div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_320px_160px] md:items-start">
            <label className="relative">
              <FiSearch className="absolute left-3 top-3 text-slate-400" />
              <ProductSearchInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                products={searchSuggestionProducts}
                className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2"
                placeholder="Buscar producto o variedad" aria-label="Buscar producto o variedad"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              <span className="sr-only">Categoría de inventario</span>
              <select aria-label="Categoría de inventario" value={selectedCategoryId}
                disabled={categoriesLoading || !categories.length}
                onChange={event => setSelectedCategoryId(event.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2">
                {!categories.length && <option value="">{categoriesLoading ? 'Cargando categorías…' : 'Sin categorías'}</option>}
                {categories.map(category => <option key={category.id} value={category.id}>{getCategoryLabel(category)}</option>)}
              </select>
            </label>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              aria-label="Estado de inventario" className="border border-slate-300 rounded-lg px-3 py-2"
            >
              <option value="all">Todos</option>
              <option value="ok">OK</option>
              <option value="negative">Negativo</option>
              <option value="warning">Revisar</option>
            </select>
          </div>}
        reports={<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Disponible</p>
            <p className="text-2xl font-bold text-slate-900">{formatKg(kpis.availableKg)}</p>
            {kpis.availablePacks !== undefined && <p className="font-semibold text-blue-700">{formatInventoryQuantity(kpis.availablePacks, 0)} packs en bodega</p>}
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Negativo</p>
            <p className="text-2xl font-bold text-red-700">{formatKg(kpis.negativeKg)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Merma</p>
            <p className="text-2xl font-bold text-slate-900">{formatKg(kpis.mermaKg)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sin conversion</p>
            <p className="text-2xl font-bold text-amber-700">{kpis.unresolvedMovements || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Estimado mayorista</p>
            <p className="flex items-center gap-1 text-2xl font-bold text-emerald-700">
              <FiDollarSign className="h-5 w-5" />
              {formatInventoryCurrency(kpis.wholesaleEstimateAmount).replace('$', '')}
            </p>
            {Number(kpis.wholesaleEstimateMissingCount || 0) > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                {kpis.wholesaleEstimateMissingCount} variedades sin precio convertible
              </p>
            )}
          </div>
        </div>}
      />
      {selectedRow && <VarietyEditorDialog title="Historial del formato" eyebrow="Inventario" placement="right" onClose={() => setSelectedRow(null)} closeLabel="Cerrar movimientos">
        <InventoryHistory key={`${branchId}:${getRowInventoryKey(selectedRow)}`} branchId={branchId} row={selectedRow} canUpdateStock={canUpdateStock} refreshKey={refreshKey} onSaved={refreshInventoryViews} />
      </VarietyEditorDialog>}
      {warehouseOperation && <WarehouseOperationPanel key={`operation:${user?.selectedCompanyId}:${branchId}`} branchId={branchId}
        mode={warehouseOperation.mode} initialRows={warehouseOperation.rows} canUpdateStock={canUpdateStock}
        onClose={() => setWarehouseOperation(null)} onSaved={async () => { setSelectionResetKey(value => value + 1); await refreshInventoryViews(); }}
        onStandardMerma={canUpdateStock ? () => { setWarehouseOperation(null); setMermaWorkspaceOpen(true); } : undefined} />}

      {conversionSetup && (
        <InventoryConversionModal
          branchId={branchId}
          row={conversionSetup.row}
          metricUnit={conversionSetup.metricUnit}
          onClose={() => setConversionSetup(null)}
          onSaved={handleInventoryConversionSaved}
        />
      )}

      {countWorkspaceOpen && (
        <InventoryCountWorkspace
          branchId={branchId}
          sessionId={countWorkspaceSessionId}
          user={user}
          categoryOptions={categories}
          initialCategoryIds={countWorkspaceSessionId ? [] : selectedCategoryIds}
          initialRowKeys={countRowKeys}
          onClose={() => setCountWorkspaceOpen(false)}
          onSaved={refreshInventoryViews}
        />
      )}

      {mermaWorkspaceOpen && (
        <BulkMermaWorkspace
          branchId={branchId}
          onClose={() => setMermaWorkspaceOpen(false)}
          onSaved={refreshInventoryViews}
        />
      )}

      {emailModalOpen && (
        <InventoryEmailModal
          branchId={branchId}
          search={search}
          status={status}
          selectedCategoryIds={selectedCategoryIds}
          categories={categories}
          totalRows={totalRows}
          kpis={kpis}
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </div>
  );
};

export default function InventoryPage() {
  const { branchId: routeBranchId } = useParams();
  const branchId = routeBranchId || localStorage.getItem('selectedBranchId');
  const { user } = useCurrentUser();
  return <InventoryBranchPage key={`${user?.selectedCompanyId}:${branchId}`} branchId={branchId} user={user} />;
}
